import { useSuspenseQuery } from '@tanstack/react-query';
import { TypeBadgeProps } from '@thefarmersfront/kitchen-mobile';
import type {
  TypeGetRecruitNoticesListData,
  TypeGetRecruitNoticesListParams,
  TypeGetRecruitNoticesListResponse,
} from '~/api/recruitNotices';
import { registerMockAPI } from '~/mocks/mockApis';

import { axiosInstance } from '~/services/fetchServiceV2';

// ── 근무환경 코드 enum ──

export type WorkEnvironmentCode =
  | 'NORMAL'
  | 'COLD'
  | 'FROZEN'
  | 'INTEGRATED_RETURN_CENTER'
  | 'HUB'
  | 'BEAUTY_PREPROCESSING_CENTER'
  | 'ALL_TEMP';

// ── 근무환경 → 뱃지 컬러 매핑 (피그마 기준) ──

export type BadgeColor = TypeBadgeProps['color'];

export const WORK_ENVIRONMENT_BADGE_COLOR: Record<WorkEnvironmentCode, BadgeColor> = {
  NORMAL: 'orange',
  COLD: 'blue',
  FROZEN: 'mint',
  ALL_TEMP: 'green',
  INTEGRATED_RETURN_CENTER: 'purple',
  BEAUTY_PREPROCESSING_CENTER: 'violet',
  HUB: 'brown',
};

// ── 시간대 필터 타입 ──

export type TimePeriod = 'ALL' | 'MORNING' | 'AFTERNOON' | 'NIGHT';

// ── 개별 항목 타입 (API DTO 기반) ──

export type WorkDescription = TypeGetRecruitNoticesListData[number];

// ── Hook ──

export const WORK_DESCRIPTIONS_QUERY_KEY = 'work-descriptions';

/** @description 모집중인 공고 목록 조회 (근무 시간/환경 선택) */
export const useGetWorkDescriptions = (params: TypeGetRecruitNoticesListParams) => {
  const { data } = useSuspenseQuery({
    queryKey: [WORK_DESCRIPTIONS_QUERY_KEY, params],
    queryFn: async () => {
      return await axiosInstance.get<TypeGetRecruitNoticesListResponse>('/recruit-notices', { params });
    },
    gcTime: 0,
    retry: 1,
    select: (response) => response.data.data ?? [],
  });

  return { data };
};

// ── Mock API ──

registerMockAPI<TypeGetRecruitNoticesListResponse>({
  method: 'get',
  endpoint: '/recruit-notices',
  label: '근무 시간, 환경 선택/모집중인 공고 목록 조회',
  scenarios: [
    {
      name: '목록 7개 (전체 뱃지)',
      response: {
        code: '200',
        message: 'OK',
        data: [
          {
            recruitNoticeId: '1',
            workShift: '08:00 ~ 17:00',
            workEnvironment: { code: 'NORMAL', text: '상온', warningMessage: null },
            dailyWage: 81636,
            mealProvisionType: { code: 'LUNCH', text: '중식제공' },
            breakTimeMinutes: 80,
          },
          {
            recruitNoticeId: '2',
            workShift: '08:00 ~ 17:00',
            workEnvironment: {
              code: 'COLD',
              text: '냉장',
              warningMessage: '냉장 근무는 인원 제한으로 배정이 변경될 수 있어요.',
            },
            dailyWage: 91974,
            mealProvisionType: { code: 'LUNCH', text: '중식제공' },
            breakTimeMinutes: 70,
          },
          {
            recruitNoticeId: '3',
            workShift: '08:00 ~ 17:00',
            workEnvironment: {
              code: 'FROZEN',
              text: '냉동',
              warningMessage: '냉동 근무는 방한 보호구를 대여해드려요.',
            },
            dailyWage: 124751,
            mealProvisionType: { code: 'LUNCH', text: '중식제공' },
            breakTimeMinutes: 70,
          },
          {
            recruitNoticeId: '4',
            workShift: '09:00 ~ 18:00',
            workEnvironment: { code: 'ALL_TEMP', text: '전체온도', warningMessage: null },
            dailyWage: 95000,
            mealProvisionType: { code: 'LUNCH', text: '중식제공' },
            breakTimeMinutes: 70,
          },
          {
            recruitNoticeId: '5',
            workShift: '09:00 ~ 18:00',
            workEnvironment: { code: 'INTEGRATED_RETURN_CENTER', text: '통합회수센터', warningMessage: null },
            dailyWage: 88000,
            mealProvisionType: { code: 'NONE', text: '미제공' },
            breakTimeMinutes: 60,
          },
          {
            recruitNoticeId: '6',
            workShift: '13:00 ~ 22:00',
            workEnvironment: { code: 'BEAUTY_PREPROCESSING_CENTER', text: '뷰티전처리센터', warningMessage: null },
            dailyWage: 81636,
            mealProvisionType: { code: 'DINNER', text: '석식제공' },
            breakTimeMinutes: 70,
          },
          {
            recruitNoticeId: '7',
            workShift: '13:00 ~ 22:00',
            workEnvironment: { code: 'HUB', text: 'HUB', warningMessage: null },
            dailyWage: 85000,
            mealProvisionType: { code: 'DINNER', text: '석식제공' },
            breakTimeMinutes: 70,
          },
        ],
      },
    },
    {
      name: '목록 1개 (단일선택)',
      response: {
        code: '200',
        message: 'OK',
        data: [
          {
            recruitNoticeId: '6',
            workShift: '13:00 ~ 22:00',
            workEnvironment: { code: 'INTEGRATED_RETURN_CENTER', text: '통합회수센터', warningMessage: null },
            dailyWage: 81636,
            mealProvisionType: { code: 'LUNCH', text: '중식제공' },
            breakTimeMinutes: 80,
          },
        ],
      },
    },
    {
      name: '목록 없음',
      response: {
        code: '200',
        message: 'OK',
        data: [],
      },
    },
    {
      name: '서버 에러',
      response: { code: '500', message: '서버 내부 오류가 발생했습니다.', data: [] },
      status: 500,
    },
  ],
});
